import React from "react";
import { Spinner } from "react-bootstrap";
import Can from "../../components/Can";
import "../technicians/ProductUnitWisePage.css";
import "./RoleFormAssignmentsPage.css";
import useFormAssignments, { STATUS_VARIANTS } from "../../hooks/useFormAssignments";
import FormAssignmentSummaryCards from "../../components/forms/FormAssignmentSummaryCards";
import AssignFormModal from "../../components/forms/AssignFormModal";
import ManageAssignedUsersModal from "../../components/forms/ManageAssignedUsersModal";
import JustAssignedLinksModal from "../../components/forms/JustAssignedLinksModal";
import PublicLinkSummaryCell from "../../components/forms/PublicLinkSummaryCell";

// Technician/Manager/Developer/Staff/other dynamic-role page for
// "Assign Form" — OWN JSX, deliberately built in the SAME visual language
// as /technician/products (ProductUnitWisePage): puws-breadcrumb eyebrow,
// puws-title/subtitle, puws-btn-ghost/puws-btn-primary actions, puws-stats
// strip, and a compact rfa-table. All data/business logic (fetch, filters,
// pagination, save/status handlers, the Assign Form modal) comes from the
// SAME useFormAssignments() hook AdminFormAssignmentsPage.jsx uses — this
// file owns presentation only.
export default function RoleFormAssignmentsPage() {
  const {
    can, permissionsLoading, hasAnyAssignmentAccess,
    saving, busyId,
    projectFilterOptions, users,
    search, setSearch, statusFilter, setStatusFilter, projectFilter, setProjectFilter,
    currentPage, setCurrentPage, pageSize,
    form, selectedFormRow, showModal,
    handleChange, openAdd, closeModal, handleFormChange, handleSave,
    setStatus,
    usersModalTarget, removeUserTarget, setRemoveUserTarget, removingUser,
    assignedUsers, addUserIds, setAddUserIds, usersLoading,
    openUsersModal, closeUsersModal, addUsersToAssignment, confirmRemoveUserFromAssignment,
    // Manage Assigned Employees modal's "Add Employees" directory search --
    // see useFormAssignments.js's own header comment on these.
    eligibleUsers, eligibleUsersTotal, eligibleUsersLoading,
    eligibleUsersSearch, searchEligibleUsers, loadMoreEligibleUsers, ELIGIBLE_USERS_PAGE_SIZE,
    // This page's table is labeled "Assignments — Assigned By Me" and must
    // ONLY ever show assignments the logged-in user actually created
    // (form_assignments.created_by = actor, enforced server-side by
    // GET /form-assignments/assigned-by-me) -- the SAME scope the KPI strip
    // above reads from (services/formAssignmentDashboardService.js's
    // getAssignerSummary). Never `assignments`/`total`/`loading`, which are
    // permission/data-scope VISIBILITY and can include assignments someone
    // else created.
    assignedByMeAssignments: assignments, assignedByMeTotal: total, assignedByMeLoading: loading,
    assignedByMeTotalPages: totalPages, fetchAssignedByMe: fetchAssignments,
    // Phase 16 (Public Form Links): the SAME per-assignee link management +
    // one-time "here are the links" surface Admin's page uses — no
    // Developer-specific copy of either, no role check anywhere here.
    // Visibility of the actual buttons is still decided by <Can> inside
    // ManageAssignedUsersModal itself, against this user's real
    // form_assignments:assign_users/remove_users grants.
    linkBusyId, generatePublicLink, regeneratePublicLink, revokePublicLink,
    justAssignedLinks, closeJustAssignedLinks, generatedLinks,
  } = useFormAssignments();

  if (permissionsLoading) {
    return <div className="puws-page rfa-theme"><div className="puws-loader"><Spinner animation="border" /></div></div>;
  }

  if (!hasAnyAssignmentAccess) {
    return (
      <div className="puws-page rfa-theme">
        <div className="puws-empty">
          <div className="puws-empty-icon"><i className="fa-solid fa-lock" aria-hidden="true"></i></div>
          <h5>Access denied</h5>
          <p>You do not have permission to assign forms. Ask an Administrator to grant you Assign Form access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="puws-page rfa-theme">

      <div className="puws-header">
        <div>
          <div className="puws-breadcrumb">Forms</div>
          <h1 className="puws-title">Assign <span>Forms</span></h1>
          <p className="puws-subtitle">Assign a published form to one or more employees and track progress.</p>
        </div>
        <div className="puws-header-actions">
          <button className="puws-btn puws-btn-ghost" onClick={fetchAssignments} disabled={loading}>
            <i className={`fa-solid fa-arrows-rotate${loading ? " fa-spin" : ""}`} aria-hidden="true"></i>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <Can moduleKey="form_assignments" actionKey="create">
            <button className="puws-btn puws-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i> Assign Form
            </button>
          </Can>
        </div>
      </div>

      <FormAssignmentSummaryCards
        statusFilterActive={!!statusFilter}
        onClearStatusFilter={() => { setCurrentPage(1); setStatusFilter(""); }}
        primaryKeys={[
          "assigner.assignedByMe",
          "assigner.pendingReviews",
          "assigner.inProgressReviews",
          "assigner.submittedReviews",
          "assigner.overdueReviews",
        ]}
      />

      <div className="rfa-card">
        <div className="rfa-toolbar">
          <div>
            <h5>Assignments — Assigned By Me</h5>
          </div>
          <div className="rfa-toolbar-actions">
            <div className="puws-search-wrap">
              <span className="puws-search-ico"><i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
              <input
                className="puws-search"
                placeholder="Search title..."
                value={search}
                onChange={(e) => { setCurrentPage(1); setSearch(e.target.value); }}
              />
            </div>
            <select className="puws-select" value={statusFilter} onChange={(e) => { setCurrentPage(1); setStatusFilter(e.target.value); }}>
              <option value="">All Statuses</option>
              {Object.keys(STATUS_VARIANTS).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="puws-select" value={projectFilter} onChange={(e) => { setCurrentPage(1); setProjectFilter(e.target.value); }}>
              <option value="">All Projects</option>
              {projectFilterOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>

        <div className="rfa-table-wrap">
          {loading ? (
            <div className="puws-loader"><Spinner animation="border" /><p>Loading assignments…</p></div>
          ) : assignments.length === 0 ? (
            <div className="puws-empty">
              <div className="puws-empty-icon"><i className="fa-solid fa-clipboard-list" aria-hidden="true"></i></div>
              <h5>No assignments created by you yet</h5>
              <p>Create an assignment to send a published form to one or more employees.</p>
              <Can moduleKey="form_assignments" actionKey="create">
                <button className="puws-btn puws-btn-primary" onClick={openAdd}>
                  <i className="fa-solid fa-plus" aria-hidden="true"></i> Assign Form
                </button>
              </Can>
            </div>
          ) : (
            <table className="rfa-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th>Assignment</th>
                  <th>Form</th>
                  <th>Project / Module</th>
                  <th>Recipients</th>
                  <th>Due</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th style={{ width: 170 }}>Public Link</th>
                  <th style={{ width: 190 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((row, index) => (
                  <tr key={row.id}>
                    <td className="rfa-muted">{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>
                      <strong>{row.assignment_title}</strong>
                      {!!row.is_mandatory && <span className="rfa-pill rfa-pill-warn">Mandatory</span>}
                      <div className="rfa-subtext">
                        {row.created_at ? `Created ${new Date(row.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}` : null}
                      </div>
                    </td>
                    <td>
                      {row.form_name} <span className="rfa-muted">v{row.version_number}</span>
                      <div className="rfa-subtext">{row.form_type_name}</div>
                    </td>
                    <td className="rfa-muted">
                      {row.project_name || "—"}
                      {row.project_module_name ? <div className="rfa-subtext">{row.project_module_name}</div> : null}
                    </td>
                    <td>{row.assigned_count} User{row.assigned_count === 1 ? "" : "s"}</td>
                    <td className="rfa-muted">{row.due_at ? new Date(row.due_at).toLocaleDateString() : "—"}</td>
                    <td>{row.submitted_count} / {row.assigned_count}</td>
                    <td><span className={`rfa-pill rfa-pill-${(STATUS_VARIANTS[row.status] || "secondary")}`}>{row.status}</span></td>
                    <td>
                      <PublicLinkSummaryCell
                        row={row}
                        linkBusyId={linkBusyId}
                        onGenerateLink={generatePublicLink}
                        onRegenerateLink={regeneratePublicLink}
                        onRevokeLink={revokePublicLink}
                        onManageLinks={openUsersModal}
                        generatedLinks={generatedLinks}
                      />
                    </td>
                    <td>
                      <div className="rfa-actions">
                        <Can moduleKey="form_assignments" actionKey="assign_users">
                          <button className="rfa-icon-btn" title="Manage Recipients & Public Links" aria-label="Manage Recipients & Public Links" onClick={() => openUsersModal(row)}>
                            <i className="fa-solid fa-users" aria-hidden="true"></i>
                          </button>
                        </Can>
                        {row.status !== "ACTIVE" && row.status !== "CLOSED" && row.status !== "CANCELLED" && can("form_assignments", "activate") && (
                          <button className="rfa-icon-btn rfa-icon-btn-primary" title="Activate" disabled={busyId === row.id} onClick={() => setStatus(row, "ACTIVE")}>
                            <i className="fa-solid fa-play" aria-hidden="true"></i>
                          </button>
                        )}
                        {row.status === "ACTIVE" && can("form_assignments", "close") && (
                          <button className="rfa-icon-btn" title="Close" disabled={busyId === row.id} onClick={() => setStatus(row, "CLOSED")}>
                            <i className="fa-solid fa-stop" aria-hidden="true"></i>
                          </button>
                        )}
                        {(row.status === "DRAFT" || row.status === "ACTIVE") && can("form_assignments", "cancel") && (
                          <button className="rfa-icon-btn rfa-icon-btn-danger" title="Cancel" disabled={busyId === row.id} onClick={() => setStatus(row, "CANCELLED")}>
                            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rfa-footer">
          <div className="rfa-muted">Showing <strong>{assignments.length}</strong> of <strong>{total}</strong></div>
          {totalPages > 1 && (
            <div className="rfa-pagination">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} aria-label="Previous page">
                <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
              </button>
              <span>{currentPage} / {totalPages}</span>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} aria-label="Next page">
                <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      <AssignFormModal
        show={showModal}
        onClose={closeModal}
        form={form}
        users={users}
        selectedFormRow={selectedFormRow}
        saving={saving}
        onChange={handleChange}
        onFormChange={handleFormChange}
        onSave={handleSave}
      />

      <ManageAssignedUsersModal
        target={usersModalTarget}
        onClose={closeUsersModal}
        loading={usersLoading}
        assignedUsers={assignedUsers}
        eligibleUsers={eligibleUsers}
        eligibleUsersTotal={eligibleUsersTotal}
        eligibleUsersLoading={eligibleUsersLoading}
        eligibleUsersPageSize={ELIGIBLE_USERS_PAGE_SIZE}
        eligibleUsersSearch={eligibleUsersSearch}
        onSearchEligibleUsers={searchEligibleUsers}
        onLoadMoreEligibleUsers={loadMoreEligibleUsers}
        addUserIds={addUserIds}
        onToggleAddUser={(id) => setAddUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
        onAddUsers={addUsersToAssignment}
        removeUserTarget={removeUserTarget}
        onRequestRemove={setRemoveUserTarget}
        onCancelRemove={() => setRemoveUserTarget(null)}
        onConfirmRemove={confirmRemoveUserFromAssignment}
        removing={removingUser}
        linkBusyId={linkBusyId}
        onGenerateLink={generatePublicLink}
        onRegenerateLink={regeneratePublicLink}
        onRevokeLink={revokePublicLink}
        generatedLinks={generatedLinks}
      />

      <JustAssignedLinksModal
        links={justAssignedLinks}
        onClose={closeJustAssignedLinks}
      />
    </div>
  );
}
